import { useNavigate, useParams } from "react-router-dom";
import { Heading, Text } from "../../../../components/common/typography";
import { Chip } from "../../../../components/common/chip";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { EmptyState } from "../../../../components/common/empty-state";

import { IconArrowRight, IconExternalLink, IconReceipt2 } from "@tabler/icons-react";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { useAdminPayment } from "../../hooks/useAdminPayment";
import {
  PaymentDirection,
  PaymentPartnerType,
  PaymentState,
  type PaymentDto,
} from "../../data/types";
import { dollarFormat } from "../../../../utils/currencies";

const DATE_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

function formatDate(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : DATE_FMT.format(d);
}

function stateChip(state: PaymentState) {
  if (state === PaymentState.PAID) return <Chip variant="approved">Paid</Chip>;
  if (state === PaymentState.POSTED) return <Chip variant="approved">Completed</Chip>;
  if (state === PaymentState.IN_PROCESS) return <Chip variant="underReview">In Process</Chip>;
  if (state === PaymentState.CANCELLED) return <Chip variant="rejected">Cancelled</Chip>;
  if (state === PaymentState.REJECTED) return <Chip variant="rejected">Rejected</Chip>;
  return <Chip variant="tertiary">Draft</Chip>;
}

function directionChip(d: PaymentDirection) {
  return d === PaymentDirection.OUTGOING
    ? <Chip variant="tertiary">Send</Chip>
    : <Chip variant="approved">Receive</Chip>;
}

function InfoRow({ label, value, children }: { label: string; value?: string; children?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b border-borderLight py-3 last:border-b-0">
      <Text variant="bodySm" tone="muted">{label}</Text>
      {children ?? <Text variant="bodySmBold" tone="dark">{value ?? "—"}</Text>}
    </div>
  );
}

export function AdminPaymentDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const numericId = Number(id);
  const validId = Number.isFinite(numericId) && numericId > 0 ? numericId : undefined;
  const { data: payment, isLoading } = useAdminPayment(validId);

  if (isLoading) {
    return (
      <div className="flex min-h-full w-full flex-col bg-bgLight px-8 py-8">
        <ShimmerLoader message="Loading payment…" lines={6} />
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="flex min-h-full w-full flex-col bg-bgLight px-8 py-8">
        <EmptyState
          icon={IconReceipt2}
          title="Payment not found"
          description="This payment may have been removed or you don't have access."
          primaryAction={{
            label: "Back to payments",
            onClick: () => navigate(AppRoutes.ADMIN_PAYMENTS),
          }}
        />
      </div>
    );
  }

  const p: PaymentDto = payment;
  const currency = p.currency ?? "QAR";
  const isSend = p.direction === PaymentDirection.OUTGOING;

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-8 py-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => navigate(AppRoutes.ADMIN_PAYMENTS)} className="hover:underline">
          <Text variant="bodyBold" tone="accent">← Payments</Text>
        </button>
        <Text variant="body" tone="muted">/</Text>
        <Text variant="body" tone="muted">{p.reference ?? `Payment #${p.id}`}</Text>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Heading variant="h2" tone="dark">{p.reference ?? `Payment #${p.id}`}</Heading>
          {stateChip(p.state)}
          {directionChip(p.direction)}
        </div>
        <Text variant="bodySm" tone="muted">{formatDate(p.date)}</Text>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Main info */}
        <div className="col-span-2 flex flex-col gap-6">
          {/* Amount card */}
          <div className="rounded-2xl border border-borderDefault bg-bgWhite px-6 py-5">
            <Text variant="bodyXs" tone="muted">{isSend ? "Amount sent" : "Amount received"}</Text>
            <Heading variant="h2" tone="dark" className="mt-1">
              {dollarFormat(p.amount, currency)}
            </Heading>
          </div>

          {/* Payment details */}
          <div className="rounded-2xl border border-borderDefault bg-bgWhite px-6 py-5">
            <Text variant="bodySmBold" tone="dark" className="mb-2">Payment Details</Text>
            <InfoRow label="Payment Reference" value={p.reference ?? "—"} />
            <InfoRow label="Date" value={formatDate(p.date)} />
            <InfoRow label="Type" children={directionChip(p.direction)} />
            <InfoRow label="Status" children={stateChip(p.state)} />
            <InfoRow label="Payment Method" value={p.journalName ?? "—"} />
            {p.memo && <InfoRow label="Memo" value={p.memo} />}
          </div>

          {/* Linked orders */}
          {(p.originRef || p.saleOrderRef) && (() => {
            const originIsSo = p.direction === PaymentDirection.INCOMING;
            const soRef = originIsSo ? p.originRef : p.saleOrderRef;
            const poRef = originIsSo ? p.saleOrderRef : p.originRef;
            return (
              <div className="rounded-2xl border border-borderDefault bg-bgWhite px-6 py-5">
                <Text variant="bodySmBold" tone="dark" className="mb-2">Linked Orders</Text>
                <div className="flex flex-col gap-2">
                  {soRef && (
                    <button
                      type="button"
                      onClick={() => navigate(AppRoutes.ADMIN_ORDER_DETAILS.replace(":id", soRef.replace(/\D/g, "")), { state: { from: `Payment ${p.reference ?? `#${p.id}`}` } })}
                      className="flex items-center justify-between rounded-xl border border-borderDefault bg-bgLight px-4 py-3 text-left transition-colors hover:border-borderDark hover:bg-bgWhite"
                    >
                      <div className="flex items-center gap-3">
                        <Text variant="bodySmMedium" tone="muted">Sale Order:</Text>
                        <Text variant="bodySmBold" tone="dark">{soRef}</Text>
                      </div>
                      <IconArrowRight size={16} className="text-textMuted" />
                    </button>
                  )}
                  {poRef && (
                    <button
                      type="button"
                      onClick={() => navigate(AppRoutes.ADMIN_PURCHASE_ORDER_DETAILS.replace(":id", poRef.replace(/\D/g, "")), { state: { from: `Payment ${p.reference ?? `#${p.id}`}` } })}
                      className="flex items-center justify-between rounded-xl border border-borderDefault bg-bgLight px-4 py-3 text-left transition-colors hover:border-borderDark hover:bg-bgWhite"
                    >
                      <div className="flex items-center gap-3">
                        <Text variant="bodySmMedium" tone="muted">Purchase Order:</Text>
                        <Text variant="bodySmBold" tone="dark">{poRef}</Text>
                      </div>
                      <IconArrowRight size={16} className="text-textMuted" />
                    </button>
                  )}
                </div>
              </div>
            );
          })()}
        </div>

        {/* Sidebar */}
        <div className="flex flex-col gap-6">
          {/* Partner card */}
          <div className="rounded-2xl border border-borderDefault bg-bgWhite px-6 py-5">
            <Text variant="bodySmBold" tone="dark" className="mb-2">
              {isSend ? "Seller" : "Buyer"}
            </Text>
            <InfoRow label="Name" value={p.partnerName ?? "—"} />
            <InfoRow label="Role" value={
              p.partnerType === PaymentPartnerType.SUPPLIER ? "Supplier"
                : p.partnerType === PaymentPartnerType.CUSTOMER ? "Customer"
                  : "Other"
            } />
          </div>

          {/* Reconciliation */}
          <div className="rounded-2xl border border-borderDefault bg-bgWhite px-6 py-5">
            <Text variant="bodySmBold" tone="dark" className="mb-2">Reconciliation</Text>
            {p.reconciledBills.length === 0 ? (
              <Text variant="bodySm" tone="muted">No invoices reconciled yet.</Text>
            ) : (
              <div className="flex flex-col gap-2">
                {p.reconciledBills.map((bill) => (
                  <div key={bill.id} className="flex items-center justify-between gap-2 rounded-xl border border-borderDefault bg-bgLight px-4 py-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <IconReceipt2 size={16} className="shrink-0 text-textMuted" />
                      <Text variant="bodySmBold" tone="dark" className="truncate">
                        {bill.name}
                      </Text>
                    </div>
                    {bill.previewUrl && (
                      <a
                        href={bill.previewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex shrink-0 items-center gap-1 text-textBody hover:text-textDark"
                      >
                        <IconExternalLink size={16} />
                        <Text variant="bodySm" tone="inherit">Open PDF</Text>
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminPaymentDetailsPage;
